import React, { useState, useEffect } from 'react';
import { uiSettingsApi } from '../api';

const DASHBOARD_LAYOUTS = [
  { value: 'standard', label: 'Standard (3 Spalten)', beschreibung: 'Klassische Ansicht mit 3 gleichbreiten Spalten' },
  { value: 'kompakt', label: 'Kompakt (4 Spalten)', beschreibung: 'Mehr Kacheln auf einen Blick' },
  { value: 'erweitert', label: 'Erweitert (2 Spalten)', beschreibung: 'Breitere Kacheln mit mehr Details' },
];

const SPRACHEN = [
  { value: 'de', label: 'Deutsch' },
  { value: 'en', label: 'English' },
];

const DATUMSFORMATE = [
  { value: 'DD.MM.YYYY', label: 'TT.MM.JJJJ (31.12.2025)' },
  { value: 'YYYY-MM-DD', label: 'JJJJ-MM-TT (2025-12-31)' },
  { value: 'MM/DD/YYYY', label: 'MM/TT/JJJJ (12/31/2025)' },
];

export default function EinstellungenPage() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [success, setSuccess] = useState(false);
  const [filters, setFilters] = useState([]);
  const [filterName, setFilterName] = useState('');
  const [filterDaten, setFilterDaten] = useState('');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [s, f] = await Promise.all([uiSettingsApi.get(), uiSettingsApi.filters()]);
        setSettings(s);
        setFilters(f.filters || []);
      } catch (e) { setErr(e.response?.data?.error || e.message); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const handleSave = async () => {
    setSaving(true); setErr(null); setSuccess(false);
    try {
      await uiSettingsApi.update(settings);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (e) { setErr(e.response?.data?.error || e.message); }
    finally { setSaving(false); }
  };

  const handleSaveFilter = async () => {
    if (!filterName.trim()) { alert('Filtername ist erforderlich.'); return; }
    let parsed;
    try { parsed = JSON.parse(filterDaten || '{}'); } catch { alert('Ungültiges JSON.'); return; }
    try {
      await uiSettingsApi.createFilter({ name: filterName, filter_daten: parsed, kontext: 'vertraege' });
      const f = await uiSettingsApi.filters();
      setFilters(f.filters || []);
      setFilterName(''); setFilterDaten('');
    } catch (e) { alert(e.response?.data?.error || e.message); }
  };

  const handleDeleteFilter = async (id) => {
    if (!window.confirm('Filter löschen?')) return;
    try {
      await uiSettingsApi.deleteFilter(id);
      setFilters(filters.filter(f => f.id !== id));
    } catch (e) { alert(e.response?.data?.error || e.message); }
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#6366f1' }}>⏳ Lade…</div>;

  const upd = (key, val) => setSettings(s => ({ ...s, [key]: val }));

  return (
    <div style={{ padding: 20, color: '#f1f5f9', maxWidth: 800 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>⚙️ Einstellungen</h1>
        <button onClick={handleSave} disabled={saving} className="btn-primary">
          {saving ? '⏳ Speichere…' : '💾 Speichern'}
        </button>
      </div>

      {err && <div style={{ background: '#7f1d1d', padding: 12, borderRadius: 8, marginBottom: 14 }}>{err}</div>}
      {success && <div style={{ background: '#052e16', border: '1px solid #22c55e', padding: 12, borderRadius: 8, marginBottom: 14, color: '#86efac' }}>✓ Einstellungen gespeichert.</div>}

      {settings && (
        <>
          {/* Darstellung */}
          <Section title="Darstellung">
            <Field label="Kompakter Modus">
              <Toggle value={settings.kompakt_modus} onChange={v => upd('kompakt_modus', v)}
                beschreibung="Reduziert Abstände und Schriftgrößen für mehr Übersicht" />
            </Field>
            <Field label="Animationen">
              <Toggle value={settings.animationen} onChange={v => upd('animationen', v)}
                beschreibung="Übergänge und Ladeanimationen" />
            </Field>
            <Field label="Dashboard-Layout">
              <div style={{ display: 'grid', gap: 8 }}>
                {DASHBOARD_LAYOUTS.map(l => (
                  <label key={l.value} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', padding: 10, borderRadius: 6, background: settings.dashboard_layout === l.value ? '#1e3a5f' : 'transparent', border: `1px solid ${settings.dashboard_layout === l.value ? '#3b82f6' : '#334155'}` }}>
                    <input type="radio" name="layout" value={l.value} checked={settings.dashboard_layout === l.value} onChange={() => upd('dashboard_layout', l.value)} style={{ marginTop: 2 }} />
                    <div>
                      <div style={{ fontWeight: 500, fontSize: 13 }}>{l.label}</div>
                      <div style={{ color: '#64748b', fontSize: 12 }}>{l.beschreibung}</div>
                    </div>
                  </label>
                ))}
              </div>
            </Field>
          </Section>

          {/* Lokalisierung */}
          <Section title="Lokalisierung">
            <Field label="Sprache">
              <select value={settings.sprache || 'de'} onChange={e => upd('sprache', e.target.value)} style={sel}>
                {SPRACHEN.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </Field>
            <Field label="Datumsformat">
              <select value={settings.datumsformat || 'DD.MM.YYYY'} onChange={e => upd('datumsformat', e.target.value)} style={sel}>
                {DATUMSFORMATE.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
              </select>
            </Field>
          </Section>

          {/* Dashboard-Sichtbarkeit */}
          <Section title="Dashboard Kacheln">
            <p style={{ color: '#64748b', fontSize: 13, margin: '0 0 12px' }}>Wähle, welche Kacheln auf dem Dashboard angezeigt werden.</p>
            {['vertraege', 'kosten', 'fristen', 'dokumente', 'aufgaben', 'kommunikation'].map(k => (
              <Field key={k} label={k.charAt(0).toUpperCase() + k.slice(1)}>
                <Toggle
                  value={!(settings.versteckte_kacheln || []).includes(k)}
                  onChange={v => {
                    const versteckt = settings.versteckte_kacheln || [];
                    upd('versteckte_kacheln', v ? versteckt.filter(x => x !== k) : [...versteckt, k]);
                  }}
                />
              </Field>
            ))}
          </Section>
        </>
      )}

      {/* Gespeicherte Filter */}
      <Section title="Gespeicherte Filter">
        <div style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
            <input value={filterName} onChange={e => setFilterName(e.target.value)} placeholder="Filtername" style={{ ...sel, flex: '1 1 120px' }} />
            <input value={filterDaten} onChange={e => setFilterDaten(e.target.value)} placeholder='{"status":"aktiv"} (JSON)' style={{ ...sel, flex: '2 1 180px' }} />
            <button onClick={handleSaveFilter} className="btn-primary" style={{ flexShrink: 0 }}>Speichern</button>
          </div>
        </div>
        {filters.length === 0 ? (
          <div style={{ color: '#475569', fontSize: 13 }}>Noch keine gespeicherten Filter.</div>
        ) : (
          <div style={{ display: 'grid', gap: 6 }}>
            {filters.map(f => (
              <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#0f172a', borderRadius: 6, padding: '8px 12px', border: '1px solid #334155' }}>
                <span style={{ flex: 1, fontSize: 13 }}>{f.name}</span>
                {f.is_default && <span style={{ color: '#6366f1', fontSize: 11 }}>★ Standard</span>}
                <span style={{ color: '#475569', fontSize: 12 }}>{f.kontext}</span>
                <button onClick={() => handleDeleteFilter(f.id)} className="btn-danger">Löschen</button>
              </div>
            ))}
          </div>
        )}
      </Section>

      <div style={{ marginTop: 24, textAlign: 'right' }}>
        <button onClick={handleSave} disabled={saving} className="btn-primary">
          {saving ? '⏳ Speichere…' : '💾 Einstellungen speichern'}
        </button>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 10, padding: 20, marginBottom: 16 }}>
      <h2 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5 }}>{title}</h2>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'flex-start', padding: '10px 0', borderBottom: '1px solid #0f172a' }}>
      <div style={{ minWidth: 140, width: 180, flexShrink: 0, color: '#94a3b8', fontSize: 13, paddingTop: 4 }}>{label}</div>
      <div style={{ flex: '1 1 180px' }}>{children}</div>
    </div>
  );
}

function Toggle({ value, onChange, beschreibung }) {
  return (
    <div>
      <div
        onClick={() => onChange(!value)}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}
      >
        <div style={{ width: 40, height: 22, background: value ? '#6366f1' : '#334155', borderRadius: 11, position: 'relative', transition: 'background 0.2s' }}>
          <div style={{ position: 'absolute', top: 3, left: value ? 20 : 3, width: 16, height: 16, background: '#fff', borderRadius: '50%', transition: 'left 0.2s' }} />
        </div>
        <span style={{ fontSize: 13, color: value ? '#a5b4fc' : '#64748b' }}>{value ? 'Ein' : 'Aus'}</span>
      </div>
      {beschreibung && <div style={{ color: '#475569', fontSize: 12, marginTop: 4 }}>{beschreibung}</div>}
    </div>
  );
}

const sel = { background: '#0f172a', border: '1px solid #334155', color: '#f1f5f9', padding: '8px 10px', borderRadius: 6, fontSize: 13, width: '100%', boxSizing: 'border-box' };
